import React from 'react';
import { CtaPerformanceSnapshot } from '../../../../services/performanceMetricsService';

// ---------------------------------------------------------------------------
// Shared mini chart pieces (same visual language as PerformanceOverview bars)
// ---------------------------------------------------------------------------

function HBarChart({ title, tooltip, data, color }: {
  title: string;
  tooltip?: string;
  data: { label: string; value: number; sub?: string }[];
  color: string;
}) {
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-3 cursor-help" title={tooltip}>
        {title}
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-gray-400">No data yet.</div>
      ) : (
        <div className="space-y-2">
          {data.map((d) => (
            <div key={d.label} className="flex items-center gap-2">
              <div className="w-36 shrink-0 text-xs text-gray-600 truncate text-right" title={d.label}>
                {d.label}
              </div>
              <div className="flex-1 bg-gray-50 rounded-full h-4 overflow-hidden">
                <div
                  className={`h-full rounded-full ${color} transition-all duration-500`}
                  style={{ width: `${(d.value / maxVal) * 100}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-gray-500 w-14 text-right">
                {d.value}{d.sub ? ` ${d.sub}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtWeekLabel(yearWeek: string): string {
  // GA4 yearWeek "202623" -> "W23"
  return `W${yearWeek.slice(4)}`;
}

function WeeklyTrend({ weekly }: { weekly: CtaPerformanceSnapshot['weekly'] }) {
  const maxVal = Math.max(...weekly.map((w) => Math.max(w.clicks, w.views)), 1);
  const barMaxH = 72;
  const hasViews = weekly.some((w) => w.views > 0);
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide cursor-help" title="Weekly totals across all CTAs. Clicks tracked since 2026-05-28; views since 2026-07-15.">
          Weekly CTA Activity
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500 inline-block" /> Clicks</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-400 inline-block" /> Views</span>
        </div>
      </div>
      <div className="flex items-end gap-2" style={{ height: barMaxH + 24 }}>
        {weekly.map((w) => (
          <div key={w.week} className="flex-1 flex flex-col items-center justify-end gap-0.5">
            <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: barMaxH }}>
              <div
                className="w-3 rounded-t bg-cyan-500"
                style={{ height: `${Math.max((w.clicks / maxVal) * barMaxH, w.clicks > 0 ? 3 : 0)}px` }}
                title={`${w.clicks} clicks`}
              />
              {hasViews && (
                <div
                  className="w-3 rounded-t bg-purple-400"
                  style={{ height: `${Math.max((w.views / maxVal) * barMaxH, w.views > 0 ? 3 : 0)}px` }}
                  title={`${w.views} views`}
                />
              )}
            </div>
            <div className="text-[9px] text-gray-400">{fmtWeekLabel(w.week)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CtaCharts — aggregate views of the CTA universe
// ---------------------------------------------------------------------------

export const CtaCharts: React.FC<{ snapshot: CtaPerformanceSnapshot }> = ({ snapshot }) => {
  const rows = snapshot.ctas;

  // Clicks by the CTA's intended funnel stage(s), combo labels kept as-is
  const byFunnel: Record<string, number> = {};
  for (const r of rows) {
    if (r.clicks === 0) continue;
    const label = r.funnel_stages.length > 0 ? r.funnel_stages.join(' + ') : 'unspecified';
    byFunnel[label] = (byFunnel[label] || 0) + r.clicks;
  }

  // Clicks by audience
  const byAudience: Record<string, number> = {};
  for (const r of rows) {
    if (r.clicks === 0) continue;
    byAudience[r.audience || 'unknown'] = (byAudience[r.audience || 'unknown'] || 0) + r.clicks;
  }

  // Clicks by destination (path only, for readability)
  const byDestination: Record<string, number> = {};
  for (const r of rows) {
    if (r.clicks === 0) continue;
    let label = r.target_url || 'unknown';
    try {
      const u = new URL(r.target_url);
      label = (u.hostname.startsWith('www.talaera') ? '' : u.hostname) + (u.pathname.replace(/\/$/, '') || '/');
    } catch { /* keep raw */ }
    byDestination[label] = (byDestination[label] || 0) + r.clicks;
  }

  // Efficiency: clicks per article placed on (only CTAs actually placed)
  const efficiency = rows
    .filter((r) => r.articles_count > 0 && r.clicks > 0)
    .map((r) => ({
      label: r.id,
      value: Math.round((r.clicks / r.articles_count) * 100) / 100,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const toSorted = (m: Record<string, number>) =>
    Object.entries(m).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));

  return (
    <div className="space-y-4">
      <WeeklyTrend weekly={snapshot.weekly} />
      <div className="grid grid-cols-2 gap-4">
        <HBarChart
          title="Clicks by Funnel Stage"
          tooltip="Total clicks grouped by the CTA's intended funnel stage(s) from the registry. Combined labels (e.g. 'mid + top') are CTAs designed for multiple stages."
          data={toSorted(byFunnel)}
          color="bg-purple-500"
        />
        <HBarChart
          title="Clicks by Audience"
          tooltip="Total clicks grouped by the CTA's target audience (B2B vs B2C)."
          data={toSorted(byAudience)}
          color="bg-cyan-500"
        />
        <HBarChart
          title="Clicks by Destination"
          tooltip="Total clicks grouped by where the CTA sends readers. Shows which offers are actually pulling traffic from the blog."
          data={toSorted(byDestination)}
          color="bg-emerald-500"
        />
        <HBarChart
          title="Clicks per Article (efficiency)"
          tooltip="Clicks divided by the number of articles the CTA appears on — which creative pulls hardest per placement. Only CTAs with at least one click shown."
          data={efficiency}
          color="bg-amber-500"
        />
      </div>
    </div>
  );
};
